<template>
  <div class="columns is-centered">
    <div class="column is-two-thirds-tablet is-half-desktop">
      <div
        id="register-form"
        class="box"
        @keyup.enter="signUp()"
        v-if="activeStep === steps.signUp"
      >
        <h1 class="title has-text-centered">
          {{ $t('register_form.title') }}
          <span class="has-text-weight-medium">{{ $t('navbar.title') }}</span>
        </h1>

        <div class="field field-help has-text-centered">
          <p class="help">{{ $t('register_form.sign_in_link.prefix') }}</p>
          <router-link to="login" class="has-text-info">{{
            $t('register_form.sign_in_link.label')
          }}</router-link>
        </div>

        <div
          class="message is-small is-danger animated fast fadeIn"
          v-if="errorMessage"
        >
          <div class="message-body">{{ $t(errorMessage) }}</div>
        </div>

        <div class="field">
          <label for="firstname" class="label">{{
            $t('register_form.firstname')
          }}</label>
          <div class="control">
            <input
              type="text"
              id="firstname"
              class="input is-shadowless"
              required
              autofocus
              v-model="firstname"
            />
          </div>
        </div>

        <div class="field">
          <label for="lastname" class="label">{{
            $t('register_form.lastname')
          }}</label>
          <div class="control">
            <input
              type="text"
              id="lastname"
              class="input is-shadowless"
              required
              v-model="lastname"
            />
          </div>
        </div>

        <div class="field">
          <label for="company" class="label">{{
            $t('register_form.company')
          }}</label>
          <div class="control">
            <input
              type="text"
              id="company"
              class="input is-shadowless"
              required
              v-model="company"
            />
          </div>
        </div>

        <div class="field">
          <label for="region" class="label">{{
            $t('register_form.region')
          }}</label>
          <div class="control">
            <select
              id="region"
              class="input is-shadowless"
              required
              v-model="region"
            >
              <option value="EMEA">Europe, Middle-East and Africa</option>
              <option value="AMER">North, Central and South America</option>
              <option value="APAC">Asia Pacific</option>
            </select>
          </div>
        </div>

        <div class="field">
          <label for="email" class="label">{{
            $t('register_form.email_label')
          }}</label>
          <div class="control">
            <input
              type="email"
              id="email"
              class="input is-shadowless"
              autocomplete="email"
              required
              v-model="email"
            />
          </div>
        </div>

        <div class="field">
          <label for="password" class="label password-label">
            <span>{{ $t('register_form.password_label') }}</span>
            <div class="tooltip-container">
              <i class="material-icons">&#xE88F;</i>
              <div class="tooltip tooltip-top" role="tooltip">
                <div class="tooltip-outer">
                  <div class="tooltip-arrow"></div>
                  <div class="tooltip-inner">
                    {{ $t('login_form.journeys_password_strength') }}
                  </div>
                </div>
              </div>
            </div>
          </label>
          <div class="control">
            <input
              type="password"
              id="password"
              class="input is-shadowless"
              required
              v-model="password"
            />
          </div>
        </div>

        <div class="field">
          <label for="confirm-password" class="label">{{
            $t('register_form.confirm_password_label')
          }}</label>
          <div class="control">
            <input
              type="password"
              id="confirm-password"
              class="input is-shadowless"
              required
              v-model="confirmPassword"
            />
          </div>
        </div>

        <div class="field">
          <div class="control">
            <p-check
              class="p-icon p-curve p-smooth is-blue"
              v-model="hasProjectCode"
            >
              <i class="icon material-icons" slot="extra">&#xE876;</i>
              {{ $t('register_form.project_code_label.prefix') }}
              <span class="has-text-weight-semibold">{{
                $t('register_form.project_code_label.label')
              }}</span>
              {{ $t('register_form.project_code_label.suffix') }}
            </p-check>
          </div>
        </div>

        <div class="field animated fast fadeIn" v-if="hasProjectCode">
          <label for="project-code" class="label">{{
            $t('register_form.project_code_placeholder')
          }}</label>
          <div class="control">
            <input
              type="text"
              id="project-code"
              class="input is-shadowless"
              :placeholder="$t('register_form.project_code_placeholder')"
              v-model="projectCode"
            />
          </div>
        </div>

        <div class="field">
          <div class="control">
            <p-check
              class="p-icon p-curve p-smooth is-blue"
              v-model="ageVerification"
            >
              <i class="icon material-icons" slot="extra">&#xE876;</i>
              {{ $t('register_form.age_label') }}
            </p-check>
          </div>
        </div>

        <div class="field">
          <div class="control">
            <p-check
              class="p-icon p-curve p-smooth is-blue"
              v-model="newsletter"
            >
              <i class="icon material-icons" slot="extra">&#xE876;</i>
              {{ $t('register_form.newsletter_label') }}
            </p-check>
            <span v-if="newsletter" class="animated fast fadeIn">🎉</span>
          </div>
        </div>

        <div class="field">
          <div class="control">
            <gradient-button
              :title="$t('register_form.register_button')"
              :is-loading="isLoading"
              @click.native="signUp()"
            ></gradient-button>
          </div>
        </div>
      </div>

      <div
        id="terms-form"
        class="box"
        @keyup.enter="acceptTerms()"
        v-if="activeStep === steps.terms"
      >
        <h1 class="title has-text-centered">
          <span class="has-text-weight-medium">{{
            $t('register_form.terms.title.label')
          }}</span>
          {{ $t('register_form.terms.title.suffix') }}
        </h1>
        <div class="content is-small">
          <h2>{{ $t('register_form.terms.content.section_1.title') }}</h2>
          <p>{{ $t('register_form.terms.content.section_1.description') }}</p>

          <h2>{{ $t('register_form.terms.content.section_2.title') }}</h2>
          <p>{{ $t('register_form.terms.content.section_2.description') }}</p>

          <h2>{{ $t('register_form.terms.content.section_3.title') }}</h2>
          <p>{{ $t('register_form.terms.content.section_3.description') }}</p>

          <h2>{{ $t('register_form.terms.content.section_4.title') }}</h2>
          <p>{{ $t('register_form.terms.content.section_4.description_1') }}</p>
          <p>{{ $t('register_form.terms.content.section_4.description_2') }}</p>
        </div>

        <div
          class="message is-small is-danger animated fast fadeIn"
          v-if="errorMessage"
        >
          <div class="message-body">{{ $t(errorMessage) }}</div>
        </div>

        <div class="field field-footer">
          <div class="control">
            <p-check class="p-icon p-curve p-smooth is-blue" v-model="terms">
              <i class="icon material-icons" slot="extra">&#xE876;</i>
              {{ $t('register_form.terms.terms_label.prefix') }}
            </p-check>
            <a
              href="https://www.sentiance.com/terms/journeys"
              target="_blank"
              class="has-text-info"
              >{{ $t('register_form.terms.terms_label.label') }}</a
            >
          </div>
        </div>

        <div class="field">
          <div class="control">
            <gradient-button
              :title="$t('register_form.register_button')"
              :is-loading="isLoading"
              @click.native="acceptTerms()"
            ></gradient-button>
          </div>
        </div>

        <div class="field field-footer is-marginless">
          <div class="control has-text-centered">
            <a class="has-text-info is-size-7" @click="goBack()">{{
              $t('register_form.terms.go_back')
            }}</a>
          </div>
        </div>
      </div>

      <div id="confirm-form" class="box" v-if="activeStep === steps.confirm">
        <h1 class="title is-size-3 has-text-centered">✉️</h1>
        <div class="content is-small">
          <h2 v-if="email">
            {{ $t('register_form.confirm.title', { email }) }}
          </h2>
          <h2 v-else>{{ $t('register_form.confirm.title_no_email') }}</h2>
          <p>{{ $t('register_form.confirm.description') }}</p>
          <p>
            {{ $t('register_form.confirm.whitelist.prefix') }}
            <a
              :href="`mailto:${$t('register_form.confirm.whitelist.label')}`"
              class="has-text-info"
              >{{ $t('register_form.confirm.whitelist.label') }}</a
            >
            {{ $t('register_form.confirm.whitelist.suffix') }}
          </p>
        </div>

        <div
          class="message is-small is-danger animated fast fadeIn"
          v-if="errorMessage"
        >
          <div class="message-body">{{ $t(errorMessage) }}</div>
        </div>

        <div
          class="message is-small is-success animated fast fadeIn"
          v-if="hasResentEmail"
        >
          <div class="message-body">
            <p>{{ $t('register_form.confirm.resend_success') }}</p>
          </div>
        </div>

        <div class="field field-action">
          <div class="control">
            <gradient-button
              :title="$t('register_form.confirm.resend_button')"
              :is-inline="true"
              @click.native="resendConfirm()"
            ></gradient-button>
            <div class="help">
              {{ $t('reset_password_form.sign_in_link.prefix') }}
              <router-link to="login" class="has-text-info">{{
                $t('reset_password_form.sign_in_link.label')
              }}</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { isLoggedIn } from 'router/helpers';
import { isEmailValid, isPasswordValid } from 'utils/auth';
import { GradientButton } from 'components/buttons';

const steps = {
  signUp: 'signUp',
  terms: 'terms',
  confirm: 'confirm',
};

export default {
  components: {
    GradientButton,
  },

  data() {
    return {
      // inputs
      firstname: '',
      lastname: '',
      company: '',
      region: 'EMEA',
      email: '',
      password: '',
      confirmPassword: '',
      projectCode: '',
      // checkboxes
      hasProjectCode: false,
      ageVerification: false,
      newsletter: false,
      terms: false,
      // utils
      errorMessage: '',
      hasResentEmail: false,
      isLoading: false,
      // steps
      steps: steps,
      activeStep: steps.signUp,
    };
  },

  beforeRouteEnter(to, from, next) {
    return isLoggedIn() ? next(false) : next();
  },

  watch: {
    $route: {
      immediate: true,
      handler(to, from) {
        const step = get(to, 'query.step');

        if (this.steps[step]) {
          this.activeStep = step;

          return;
        }

        this.activeStep = steps.signUp;
        this.errorMessage = '';
      },
    },
  },

  methods: {
    signUp() {
      if (
        this.firstname === '' ||
        this.lastname === '' ||
        this.company === '' ||
        this.email === '' ||
        this.password === '' ||
        this.confirmPassword === ''
      ) {
        this.errorMessage = 'register_form.errors.empty_field';
        return;
      }

      if (!isEmailValid(this.email)) {
        this.errorMessage = 'register_form.errors.invalid_email';
        return;
      }

      if (this.password !== this.confirmPassword) {
        this.errorMessage = 'register_form.errors.match_passwords';
        return;
      }

      if (!isPasswordValid(this.password, 3)) {
        this.errorMessage = 'login_form.journeys_password_strength';
        return;
      }

      if (this.hasProjectCode && this.projectCode.length === 0) {
        this.errorMessage = 'register_form.errors.empty_project_code';
        return;
      }

      if (!this.ageVerification) {
        this.errorMessage = 'register_form.errors.age_verification';
        return;
      }

      this.errorMessage = '';
      this.$router.push({
        query: {
          step: 'terms',
        },
      });
    },

    acceptTerms() {
      if (this.isLoading) {
        return;
      }

      if (!this.terms) {
        this.errorMessage = 'register_form.terms.errors.accept_terms';
        return;
      }

      this.isLoading = true;

      Auth.register(
        this.firstname,
        this.lastname,
        this.company,
        this.region,
        this.email,
        this.password,
        this.projectCode,
        this.newsletter
      )
        .then(res => {
          this.errorMessage = '';

          if (this.projectCode && this.projectCode.length > 0) {
            this.$router.push('login');

            return;
          }

          this.$router.push({
            query: {
              step: 'confirm',
            },
          });
        })
        .catch(err => {
          this.errorMessage = err.message || err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    resendConfirm() {
      this.hasResentEmail = false;

      Auth.resendConfirmation(this.email).then(
        res => {
          this.errorMessage = '';
          this.hasResentEmail = true;
        },
        err => {
          this.errorMessage = err.message;
        }
      );
    },

    goBack() {
      this.errorMessage = '';
      this.terms = false;
      this.$router.push({ query: {} });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';
@import '~scss/utilities/mixins.scss';

.password-label {
  display: flex;
  align-items: center;
}

.tooltip-container {
  display: inline-flex;
  margin-left: 0.5rem;

  .material-icons {
    font-size: 0.8125rem;
  }
}

.field {
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
}

.help {
  display: inline-block;
}

.field-help {
  font-size: 0.8125rem;
}

label[for='project-code'] {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.content {
  h2 {
    font-size: 0.875rem;
    font-weight: $weight-semibold;
    letter-spacing: 0.2px;
  }
}

.field-action {
  .control {
    display: flex;
    align-items: center;

    @include until($tablet) {
      flex-direction: column;
    }

    .help {
      margin-left: 0.5rem;
    }
  }
}

.field-footer {
  .control {
    font-size: 0.875rem;
  }
}

.pretty {
  @include until($tablet) {
    white-space: initial;
  }
}
</style>

<style lang="scss">
#register-form {
  .pretty {
    .state {
      label {
        line-height: 1.5;
      }
    }
  }
}
</style>
